import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, ArrowLeft, Lock, Tag, X, Check, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
    const navigate = useNavigate();
    const { cart } = useCart();
    const [loading, setLoading] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [myCoupons, setMyCoupons] = useState([]);
    const [formData, setFormData] = useState({
        shipping_address: '',
        shipping_city: '',
        shipping_postal_code: '',
        shipping_country: 'Italia'
    });

    useEffect(() => {
        // Fetch user's available coupons
        const fetchMyCoupons = async () => {
            try {
                const response = await axios.get(`${API}/api/my-coupons`);
                setMyCoupons(response.data);
            } catch {
                setMyCoupons([]);
            }
        };
        fetchMyCoupons();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;
        
        setValidatingCoupon(true);
        try {
            const response = await axios.post(`${API}/api/coupons/validate`, 
                { code: couponCode.trim() },
            );
            setAppliedCoupon(response.data);
            toast.success(`Coupon applicato! Sconto: €${response.data.discount_amount.toFixed(2)}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Coupon non valido');
            setAppliedCoupon(null);
        } finally {
            setValidatingCoupon(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
    };

    const handleSelectCoupon = (code) => {
        setCouponCode(code);
        handleApplyCouponWithCode(code);
    };

    const handleApplyCouponWithCode = async (code) => {
        setValidatingCoupon(true);
        try {
            const response = await axios.post(`${API}/api/coupons/validate`, 
                { code },
            );
            setAppliedCoupon(response.data);
            toast.success(`Coupon applicato! Sconto: €${response.data.discount_amount.toFixed(2)}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Coupon non valido');
            setAppliedCoupon(null);
        } finally {
            setValidatingCoupon(false);
        }
    };

    const handleStripeCheckout = async () => {
        if (!formData.shipping_address || !formData.shipping_city || !formData.shipping_postal_code) {
            toast.error('Compila tutti i campi di spedizione');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API}/api/checkout/stripe`, {
                ...formData,
                payment_method: 'stripe',
                coupon_code: appliedCoupon?.code || null
            });

            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore durante il checkout');
        } finally {
            setLoading(false);
        }
    };

    const subtotal = cart.total;
    const shipping = subtotal >= 50 ? 0 : 4.99;
    const discount = appliedCoupon?.discount_amount || 0;
    const total = Math.max(0, subtotal + shipping - discount);

    if (cart.items.length === 0) {
        navigate('/cart');
        return null;
    }

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <button
                    onClick={() => navigate('/cart')}
                    className="flex items-center gap-2 text-muted hover:text-white mb-8"
                    data-testid="back-to-cart"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Torna al carrello
                </button>

                <h1 className="text-3xl font-bold mb-8">Checkout</h1>

                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Shipping Form */}
                    <div className="space-y-6">
                        <div className="bg-surface border border-border rounded-xl p-6">
                            <h2 className="text-xl font-semibold mb-4">Indirizzo di spedizione</h2>
                            
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="shipping_address">Indirizzo</Label>
                                    <Input
                                        id="shipping_address"
                                        name="shipping_address"
                                        value={formData.shipping_address}
                                        onChange={handleChange}
                                        placeholder="Via Giuseppe Atzori 44/6"
                                        className="bg-surface-elevated border-border mt-1"
                                        data-testid="shipping-address"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="shipping_city">Città</Label>
                                        <Input
                                            id="shipping_city"
                                            name="shipping_city"
                                            value={formData.shipping_city}
                                            onChange={handleChange}
                                            placeholder="Nocera Inferiore"
                                            className="bg-surface-elevated border-border mt-1"
                                            data-testid="shipping-city"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="shipping_postal_code">CAP</Label>
                                        <Input
                                            id="shipping_postal_code"
                                            name="shipping_postal_code"
                                            value={formData.shipping_postal_code}
                                            onChange={handleChange}
                                            placeholder="84014"
                                            className="bg-surface-elevated border-border mt-1"
                                            data-testid="shipping-postal-code"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="shipping_country">Paese</Label>
                                    <Input
                                        id="shipping_country"
                                        name="shipping_country"
                                        value={formData.shipping_country}
                                        onChange={handleChange}
                                        className="bg-surface-elevated border-border mt-1"
                                        data-testid="shipping-country"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Coupon Section */}
                        <div className="bg-surface border border-border rounded-xl p-6">
                            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                                <Tag className="w-5 h-5 text-primary" />
                                Codice Sconto
                            </h2>
                            
                            {appliedCoupon ? (
                                <div className="flex items-center justify-between p-3 bg-success/10 border border-success/30 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <Check className="w-5 h-5 text-success" />
                                        <span className="font-medium">{appliedCoupon.code}</span>
                                        <span className="text-success">-{appliedCoupon.discount_percent}%</span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleRemoveCoupon}
                                        className="hover:bg-danger/20 text-danger"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Inserisci codice sconto"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                            className="bg-surface-elevated border-border"
                                            data-testid="coupon-input"
                                        />
                                        <Button
                                            onClick={handleApplyCoupon}
                                            disabled={validatingCoupon || !couponCode.trim()}
                                            className="bg-primary hover:bg-primary-hover"
                                            data-testid="apply-coupon-btn"
                                        >
                                            {validatingCoupon ? '...' : 'Applica'}
                                        </Button>
                                    </div>
                                    
                                    {myCoupons.length > 0 && (
                                        <div>
                                            <p className="text-sm text-muted mb-2">I tuoi coupon disponibili:</p>
                                            <div className="space-y-2">
                                                {myCoupons.map((coupon) => (
                                                    <button
                                                        key={coupon.id}
                                                        onClick={() => handleSelectCoupon(coupon.code)}
                                                        className="w-full p-3 bg-surface-elevated border border-border rounded-lg text-left hover:border-primary/50 transition-colors"
                                                        data-testid={`coupon-${coupon.code}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <Gift className="w-4 h-4 text-primary" />
                                                                <span className="font-mono font-bold">{coupon.code}</span>
                                                            </div>
                                                            <span className="text-primary font-semibold">-{coupon.discount_percent}%</span>
                                                        </div>
                                                        {coupon.type === 'welcome' && (
                                                            <p className="text-xs text-muted mt-1">Sconto di benvenuto</p>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Payment */}
                        <div className="bg-surface border border-border rounded-xl p-6">
                            <h2 className="text-xl font-semibold mb-4">Metodo di pagamento</h2>
                            
                            <Button
                                className="w-full btn-primary py-6 text-lg"
                                onClick={handleStripeCheckout}
                                disabled={loading}
                                data-testid="pay-with-stripe"
                            >
                                {loading ? (
                                    'Caricamento...'
                                ) : (
                                    <>
                                        <CreditCard className="w-5 h-5 mr-2" />
                                        Paga con carta (Stripe)
                                    </>
                                )}
                            </Button>

                            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted">
                                <Lock className="w-4 h-4" />
                                Pagamento sicuro con crittografia SSL
                            </div>
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div>
                        <div className="bg-surface border border-border rounded-xl p-6 sticky top-24">
                            <h2 className="text-xl font-semibold mb-4">Riepilogo ordine</h2>
                            
                            <div className="space-y-4 mb-6">
                                {cart.items.map((item) => (
                                    <div key={item.product_id} className="flex gap-3">
                                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface-elevated flex-shrink-0">
                                            <img
                                                src={item.product?.image_url}
                                                alt={item.product?.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium line-clamp-1">{item.product?.name}</p>
                                            <p className="text-sm text-muted">Qtà: {item.quantity}</p>
                                        </div>
                                        <p className="font-semibold">€{item.item_total?.toFixed(2)}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="border-t border-border pt-4 space-y-2">
                                <div className="flex justify-between text-muted">
                                    <span>Subtotale</span>
                                    <span>€{subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-muted">
                                    <span>Spedizione</span>
                                    <span>{shipping === 0 ? 'Gratuita' : `€${shipping.toFixed(2)}`}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-success">
                                        <span>Sconto ({appliedCoupon?.code})</span>
                                        <span>-€{discount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                                    <span>Totale</span>
                                    <span className="text-primary" data-testid="checkout-total">€{total.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
